import { apiFetch } from "./client";

export interface LatestVersionInfo {
  version: string;
  tag: string;
  publishedAt: string;
  releaseNotes: string;
  htmlUrl: string;
  source: "github" | "npm" | "cache";
}

export interface AgentVersionInfo {
  agentId: string;
  agentName: string;
  companyId: string;
  companyName: string;
  status: string;
  currentVersion: string | null;
  latestVersion: string;
  updateAvailable: boolean;
  ipAddress: string | null;
  lastCheckedAt: string | null;
}

export interface UpdateResult {
  success: boolean;
  agentId: string;
  previousVersion: string | null;
  newVersion: string | null;
  steps: Array<{ step: string; success: boolean; output?: string; error?: string }>;
  error?: string;
}

export type UpdateJobStatus = "running" | "success" | "failed";

export interface UpdateJob {
  agentId: string;
  status: UpdateJobStatus;
  startedAt: string;
  finishedAt: string | null;
  previousVersion: string | null;
  newVersion: string | null;
  steps: Array<{ step: string; success: boolean; output?: string; error?: string }>;
  error?: string;
}

export interface VersionCheckResult {
  version: string | null;
  error?: string;
}

export const agentUpdatesApi = {
  getLatestVersion: () =>
    apiFetch<LatestVersionInfo>("/agent-updates/latest-version"),

  listAgentVersions: () =>
    apiFetch<AgentVersionInfo[]>("/agent-updates/agents"),

  checkAgentVersion: (agentId: string) =>
    apiFetch<VersionCheckResult>(`/agent-updates/agents/${agentId}/check-version`, {
      method: "POST",
    }),

  startUpdateAgent: (agentId: string) =>
    apiFetch<UpdateJob>(`/agent-updates/agents/${agentId}/update`, {
      method: "POST",
    }),

  getUpdateStatus: (agentId: string) =>
    apiFetch<UpdateJob>(`/agent-updates/agents/${agentId}/update-status`),

  // Kicks off the update and polls until it resolves. Server runs the work
  // detached from the HTTP request so we don't hit App Service's ~230 s
  // gateway timeout for the 3–5 min npm-install + restart sequence.
  updateAgent: async (
    agentId: string,
    opts: { pollIntervalMs?: number; onProgress?: (job: UpdateJob) => void } = {},
  ): Promise<UpdateResult> => {
    const pollIntervalMs = opts.pollIntervalMs ?? 2000;
    let job = await agentUpdatesApi.startUpdateAgent(agentId);
    opts.onProgress?.(job);
    while (job.status === "running") {
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      job = await agentUpdatesApi.getUpdateStatus(agentId);
      opts.onProgress?.(job);
    }
    return {
      success: job.status === "success",
      agentId: job.agentId,
      previousVersion: job.previousVersion,
      newVersion: job.newVersion,
      steps: job.steps,
      error: job.error,
    };
  },

  checkAllVersions: () =>
    apiFetch<{ results: Array<{ agentId: string; name: string; version: string | null; error?: string }> }>(
      "/agent-updates/check-all",
      { method: "POST" },
    ),
};
